import React, { useEffect, useState, useMemo } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
    Sandwich, Search, Plus, Edit2, Trash2, Copy, X, ChefHat, Database,
    Filter, AlertTriangle, ShieldAlert, RefreshCw, Globe, Tag as TagIcon, ListPlus,
    CheckSquare, Square,
} from "lucide-react";

const ALLERGEN_OPTIONS = [
    { code: "gluten", label: "Glutine" },
    { code: "eggs", label: "Uova" },
    { code: "lactose", label: "Lattosio" },
    { code: "fish", label: "Pesce" },
    { code: "crustaceans", label: "Crostacei" },
    { code: "molluscs", label: "Molluschi" },
    { code: "peanuts", label: "Arachidi" },
    { code: "nuts", label: "Frutta a guscio" },
    { code: "soy", label: "Soia" },
    { code: "celery", label: "Sedano" },
    { code: "mustard", label: "Senape" },
    { code: "sesame", label: "Sesamo" },
    { code: "sulfites", label: "Solfiti" },
    { code: "lupin", label: "Lupini" },
];

const COURSE_OPTIONS = ["antipasto", "primo", "secondo", "contorno", "dolce", "pizza", "bevanda"];
const DEPT_OPTIONS = ["cucina", "bar", "pizzeria"];

const DEPT_COLOR = (d) => ({
    cucina: "text-amber-400 bg-amber-900/30 border-amber-500/40",
    bar: "text-cyan-400 bg-cyan-900/30 border-cyan-500/40",
    pizzeria: "text-red-400 bg-red-900/30 border-red-500/40",
}[(d || "").toLowerCase()] || "text-elegant bg-deep border-[#1f2c44]");

const PiattiLibrary = () => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    // Filtri
    const [q, setQ] = useState("");
    const [fDept, setFDept] = useState("");
    const [fCourse, setFCourse] = useState("");
    const [fOrigin, setFOrigin] = useState("");
    const [fTag, setFTag] = useState("");
    const [fSource, setFSource] = useState("");
    const [fExcludeAllergens, setFExcludeAllergens] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);

    // Modali
    const [editFor, setEditFor] = useState(null);   // piatto da editare o "new"
    const [addToMenuFor, setAddToMenuFor] = useState(null);
    const [seedModal, setSeedModal] = useState(false);
    const [wipeModal, setWipeModal] = useState(false);

    // Tag preset/in-use
    useEffect(() => {
        api.get("/menu/item-tags").then(({ data }) => {
            const merged = [...new Set([...(data.presets || []), ...(data.in_use || [])])];
            setAvailableTags(merged);
        }).catch(() => {});
    }, []);

    const loadStats = async () => {
        try {
            const { data } = await api.get("/dish-library/stats");
            setStats(data);
        } catch (e) { /* silent */ }
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (fDept) params.set("department", fDept);
            if (fCourse) params.set("course", fCourse);
            if (fOrigin) params.set("origin", fOrigin);
            if (fTag) params.set("tag", fTag);
            if (fSource) params.set("source", fSource);
            if (fExcludeAllergens.length > 0) params.set("exclude_allergens", fExcludeAllergens.join(","));
            const { data } = await api.get(`/dish-library?${params}`);
            setItems(data.items);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
        await loadStats();
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fDept, fCourse, fOrigin, fTag, fSource, fExcludeAllergens]);
    useEffect(() => {
        const t = setTimeout(() => load(), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const onLoadSeed = async () => {
        try {
            const { data } = await api.post("/dish-library/seed");
            toast.success(`Seed caricato: ${data.inserted} nuovi piatti, ${data.refreshed} aggiornati`);
            setSeedModal(false);
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const onRemoveSeed = async () => {
        if (!window.confirm("Rimuovere tutti i 258 piatti predefiniti? I tuoi piatti utente NON verranno toccati.")) return;
        try {
            const { data } = await api.delete("/dish-library/seed/all");
            toast.success(`${data.removed} piatti predefiniti rimossi`);
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const onWipeUser = async () => {
        try {
            const { data } = await api.post("/dish-library/wipe-user", { confirm: true });
            toast.success(`${data.removed} piatti utente eliminati. Seed preservato.`);
            setWipeModal(false);
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const onDelete = async (item) => {
        if (!window.confirm(`Eliminare "${item.name}" dalla libreria?`)) return;
        try {
            await api.delete(`/dish-library/${item.id}`);
            toast.success("Piatto eliminato");
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const onDuplicate = async (item) => {
        try {
            const { data } = await api.post(`/dish-library/${item.id}/duplicate`);
            toast.success(`Duplicato: "${data.name}"`);
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const toggleExcludeAllergen = (code) => {
        setFExcludeAllergens((cur) => cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code]);
    };

    const clearFilters = () => {
        setQ(""); setFDept(""); setFCourse(""); setFOrigin(""); setFTag(""); setFSource(""); setFExcludeAllergens([]);
    };

    const hasFilters = !!(q || fDept || fCourse || fOrigin || fTag || fSource || fExcludeAllergens.length);

    return (
        <div className="space-y-4" data-testid="piatti-library-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="font-display text-3xl font-bold flex items-center gap-2">
                        <Database className="w-7 h-7 text-amber-400" /> Libreria Piatti
                    </h1>
                    <p className="text-elegant text-sm mt-1">Catalogo globale dei tuoi piatti — pesca da qui per creare i menu.</p>
                </div>
                <div className="flex gap-2 items-center">
                    {stats && (
                        <div className="text-xs text-elegant flex gap-3 mr-2">
                            <span><strong className="text-warm">{stats.total}</strong> piatti</span>
                            <span className="text-amber-300">{stats.by_source.seed} default</span>
                            <span className="text-emerald-300">{stats.by_source.user} tuoi</span>
                        </div>
                    )}
                    <button onClick={() => setEditFor("new")} data-testid="piatti-new"
                        className="h-10 px-4 bg-brand text-deep font-bold rounded-md text-xs uppercase tracking-wider flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Nuovo piatto
                    </button>
                </div>
            </div>

            {/* Filtri */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-[240px] flex items-center bg-deep border border-[#1f2c44] rounded">
                        <Search className="w-4 h-4 ml-3 text-elegant" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca per nome piatto…"
                            data-testid="piatti-search"
                            className="flex-1 h-10 px-3 bg-transparent text-warm text-sm focus:outline-none"
                        />
                        {q && <button onClick={() => setQ("")} className="px-3 text-elegant hover:text-red-300"><X className="w-4 h-4" /></button>}
                    </div>

                    <select value={fDept} onChange={(e) => setFDept(e.target.value)} data-testid="filter-dept"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutti i reparti</option>
                        {DEPT_OPTIONS.map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                    </select>

                    <select value={fCourse} onChange={(e) => setFCourse(e.target.value)} data-testid="filter-course"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutte le portate</option>
                        {COURSE_OPTIONS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                    </select>

                    <select value={fOrigin} onChange={(e) => setFOrigin(e.target.value)} data-testid="filter-origin"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutte le origini</option>
                        <option value="italian">🇮🇹 Italiani</option>
                        <option value="international">🌍 Internazionali</option>
                    </select>

                    <select value={fTag} onChange={(e) => setFTag(e.target.value)} data-testid="filter-tag"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutti i tag</option>
                        {availableTags.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select value={fSource} onChange={(e) => setFSource(e.target.value)} data-testid="filter-source"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutti</option>
                        <option value="seed">Default</option>
                        <option value="user">Solo miei</option>
                    </select>

                    {hasFilters && (
                        <button onClick={clearFilters} data-testid="clear-filters"
                            className="h-10 px-3 text-[10px] uppercase tracking-wider text-red-300 hover:text-red-200 border border-red-500/40 rounded">
                            Reset
                        </button>
                    )}
                </div>

                <div className="flex items-start gap-2 flex-wrap pt-3 border-t border-[#1f2c44]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-elegant flex items-center gap-1 pt-1">
                        <Filter className="w-3 h-3" /> Escludi allergeni:
                    </span>
                    <div className="flex flex-wrap gap-1">
                        {ALLERGEN_OPTIONS.map((a) => (
                            <button key={a.code} onClick={() => toggleExcludeAllergen(a.code)}
                                data-testid={`exclude-allerg-${a.code}`}
                                className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold border transition-colors ${
                                    fExcludeAllergens.includes(a.code)
                                        ? "border-red-400 bg-red-900/40 text-red-200"
                                        : "border-[#1f2c44] text-elegant hover:border-red-500/60"
                                }`}>
                                {a.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Lista piatti */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                {loading ? (
                    <div className="text-center py-12 text-elegant text-sm">Caricamento…</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-12 text-elegant text-sm">
                        {stats?.total === 0 ? (
                            <div>
                                <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <div className="mb-2">La libreria è vuota.</div>
                                <button onClick={() => setSeedModal(true)} data-testid="empty-load-seed"
                                    className="mt-3 h-10 px-4 bg-amber-500 text-deep font-bold rounded text-xs uppercase tracking-wider">
                                    Carica 258 piatti predefiniti
                                </button>
                            </div>
                        ) : "Nessun piatto trovato coi filtri correnti."}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="piatti-grid">
                        {items.map((it) => (
                            <DishCard
                                key={it.id}
                                dish={it}
                                onEdit={() => setEditFor(it)}
                                onDuplicate={() => onDuplicate(it)}
                                onDelete={() => onDelete(it)}
                                onAddToMenu={() => setAddToMenuFor(it)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Zona Pericolosa */}
            <div className="bg-red-950/10 border-2 border-red-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-4 h-4 text-red-300" />
                    <div className="font-bold text-red-200 text-sm">Gestione libreria</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => setSeedModal(true)} data-testid="open-seed-modal"
                        className="h-10 px-4 border border-amber-500/50 bg-amber-900/20 hover:bg-amber-900/50 text-amber-200 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                        <RefreshCw className="w-3 h-3" /> Ripristina seed default
                    </button>
                    <button onClick={onRemoveSeed} data-testid="remove-seed-btn"
                        className="h-10 px-4 border border-orange-500/50 hover:bg-orange-900/30 text-orange-300 rounded text-xs font-bold uppercase tracking-wider">
                        Rimuovi solo seed
                    </button>
                    <button onClick={() => setWipeModal(true)} data-testid="open-wipe-modal"
                        className="h-10 px-4 border border-red-500/50 bg-red-900/20 hover:bg-red-900/50 text-red-200 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                        <Trash2 className="w-3 h-3" /> Pulisci libreria utente
                    </button>
                </div>
            </div>

            {/* Modali */}
            {editFor && (
                <DishEditor
                    dish={editFor === "new" ? null : editFor}
                    onClose={() => setEditFor(null)}
                    onSaved={() => { setEditFor(null); load(); }}
                    availableTags={availableTags}
                />
            )}
            {addToMenuFor && (
                <AddToMenuModal
                    dish={addToMenuFor}
                    onClose={() => setAddToMenuFor(null)}
                    onAdded={() => { setAddToMenuFor(null); load(); }}
                />
            )}
            {seedModal && (
                <ConfirmModal
                    title="Ripristina seed default"
                    icon={RefreshCw}
                    iconColor="text-amber-300"
                    borderColor="border-amber-500"
                    body={
                        <>
                            <p className="mb-3">Verranno caricati <strong>258 piatti predefiniti</strong> (207 italiani + 51 internazionali) con ingredienti e allergeni.</p>
                            <p className="text-xs text-elegant">Se il seed è già presente, viene solo aggiornato. I <strong>tuoi piatti utente</strong> NON vengono toccati.</p>
                        </>
                    }
                    confirmLabel="Carica seed"
                    confirmColor="bg-amber-500 text-deep"
                    onCancel={() => setSeedModal(false)}
                    onConfirm={onLoadSeed}
                    testid="seed-modal"
                />
            )}
            {wipeModal && (
                <ConfirmModal
                    title="Pulisci libreria utente"
                    icon={Trash2}
                    iconColor="text-red-300"
                    borderColor="border-red-500"
                    body={
                        <>
                            <p className="mb-3">Verranno <strong className="text-red-200">eliminati TUTTI i piatti utente</strong> che hai creato/importato.</p>
                            <p className="text-xs text-emerald-300">I 258 piatti predefiniti (seed) NON verranno toccati.</p>
                            <p className="text-xs text-elegant mt-2">⚠ Operazione irreversibile.</p>
                        </>
                    }
                    confirmLabel="Pulisci utente"
                    confirmColor="bg-red-700 text-white"
                    onCancel={() => setWipeModal(false)}
                    onConfirm={onWipeUser}
                    testid="wipe-modal"
                />
            )}
        </div>
    );
};

// ============ DISH CARD ============
const DishCard = ({ dish, onEdit, onDuplicate, onDelete, onAddToMenu }) => {
    const isSeed = dish.source === "seed";
    return (
        <div className={`bg-deep border rounded-lg p-3 hover:border-amber-500/60 transition-colors flex flex-col ${isSeed ? "border-[#1f2c44]" : "border-emerald-500/30"}`}
            data-testid={`dish-card-${dish.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-warm leading-tight">{dish.name}</div>
                    {dish.description && <div className="text-[11px] text-elegant mt-1 line-clamp-2">{dish.description}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {isSeed
                        ? <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-900/40 text-amber-200 border border-amber-500/40 px-1.5 py-0.5 rounded">Default</span>
                        : <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-900/40 text-emerald-200 border border-emerald-500/40 px-1.5 py-0.5 rounded">Tuo</span>
                    }
                    {dish.origin === "italian" && <span className="text-[10px]" title="Italiano">🇮🇹</span>}
                    {dish.origin === "international" && <span className="text-[10px]" title="Internazionale">🌍</span>}
                </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-2">
                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${DEPT_COLOR(dish.department)}`}>{dish.department}</span>
                <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border border-[#1f2c44] text-elegant">{dish.course}</span>
                {dish.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-900/30 border border-purple-500/30 text-purple-300">{t}</span>
                ))}
            </div>

            {dish.ingredients?.length > 0 && (
                <div className="text-[10px] text-elegant mb-2 line-clamp-2">
                    <strong className="text-amber-300/80">Ingr:</strong> {dish.ingredients.join(", ")}
                </div>
            )}

            {dish.allergens?.length > 0 && (
                <div className="text-[10px] text-red-300/80 mb-3">
                    <strong>Allergeni:</strong> {dish.allergens.map((a) => ALLERGEN_OPTIONS.find((o) => o.code === a)?.label || a).join(", ")}
                </div>
            )}

            <div className="mt-auto pt-2 border-t border-[#1f2c44] flex items-center justify-between gap-1">
                <div className="text-xs text-elegant font-mono">
                    {dish.suggested_price ? `€ ${dish.suggested_price.toFixed(2)}` : "—"}
                </div>
                <div className="flex gap-1">
                    <button onClick={onAddToMenu} title="Aggiungi al menu" data-testid={`addmenu-${dish.id}`}
                        className="w-7 h-7 rounded border border-emerald-500/40 hover:bg-emerald-900/30 text-emerald-300 flex items-center justify-center">
                        <ListPlus className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={onDuplicate} title="Duplica" data-testid={`dup-${dish.id}`}
                        className="w-7 h-7 rounded border border-cyan-500/40 hover:bg-cyan-900/30 text-cyan-300 flex items-center justify-center">
                        <Copy className="w-3.5 h-3.5" />
                    </button>
                    {!isSeed && (
                        <>
                            <button onClick={onEdit} title="Modifica" data-testid={`edit-${dish.id}`}
                                className="w-7 h-7 rounded border border-amber-500/40 hover:bg-amber-900/30 text-amber-300 flex items-center justify-center">
                                <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={onDelete} title="Elimina" data-testid={`del-${dish.id}`}
                                className="w-7 h-7 rounded border border-red-500/40 hover:bg-red-900/30 text-red-300 flex items-center justify-center">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ DISH EDITOR MODAL ============
const DishEditor = ({ dish, onClose, onSaved, availableTags }) => {
    const isNew = !dish;
    const [form, setForm] = useState(
        dish || { name: "", description: "", ingredients: [], allergens: [], tags: [], department: "cucina", course: "primo", origin: null, suggested_price: "" }
    );
    const [newIng, setNewIng] = useState("");
    const [busy, setBusy] = useState(false);

    const addIngredient = () => {
        const v = newIng.trim();
        if (!v) return;
        setForm({ ...form, ingredients: [...(form.ingredients || []), v] });
        setNewIng("");
    };

    const removeIngredient = (idx) => {
        setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) });
    };

    const toggleAllergen = (code) => {
        const cur = form.allergens || [];
        setForm({ ...form, allergens: cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code] });
    };

    const toggleTag = (t) => {
        const cur = form.tags || [];
        const lc = t.toLowerCase();
        const has = cur.some((x) => x.toLowerCase() === lc);
        setForm({ ...form, tags: has ? cur.filter((x) => x.toLowerCase() !== lc) : [...cur, t] });
    };

    const submit = async () => {
        if (!form.name?.trim()) { toast.error("Nome obbligatorio"); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            if (payload.suggested_price === "" || payload.suggested_price == null) delete payload.suggested_price;
            else payload.suggested_price = parseFloat(payload.suggested_price);
            if (isNew) {
                await api.post("/dish-library", payload);
                toast.success("Piatto creato");
            } else {
                await api.patch(`/dish-library/${dish.id}`, payload);
                toast.success("Piatto aggiornato");
            }
            onSaved();
        } catch (e) { toast.error(formatError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="dish-editor-modal">
            <div className="bg-surface border-2 border-amber-500/70 rounded-lg p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-bold flex items-center gap-2">
                        <Sandwich className="w-5 h-5 text-amber-400" />
                        {isNew ? "Nuovo piatto" : `Modifica: ${dish.name}`}
                    </h2>
                    <button onClick={onClose} className="text-elegant hover:text-red-300"><X className="w-5 h-5" /></button>
                </div>

                <div className="space-y-3">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Nome piatto *" maxLength={120}
                        data-testid="editor-name"
                        className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-amber-500 outline-none" />

                    <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Descrizione" rows={2}
                        className="w-full px-3 py-2 bg-deep border border-[#1f2c44] rounded text-warm focus:border-amber-500 outline-none text-sm" />

                    <div className="grid grid-cols-3 gap-2">
                        <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                            className="h-11 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-sm">
                            {DEPT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}
                            className="h-11 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-sm">
                            {COURSE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={form.origin || ""} onChange={(e) => setForm({ ...form, origin: e.target.value || null })}
                            className="h-11 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-sm">
                            <option value="">Origine —</option>
                            <option value="italian">Italiana</option>
                            <option value="international">Internazionale</option>
                        </select>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-1">Prezzo suggerito (opzionale)</div>
                        <div className="flex items-center bg-deep border border-[#1f2c44] rounded">
                            <span className="px-3 text-elegant">€</span>
                            <input type="number" step="0.10" min="0"
                                value={form.suggested_price ?? ""} onChange={(e) => setForm({ ...form, suggested_price: e.target.value })}
                                placeholder="0,00"
                                className="flex-1 h-11 bg-transparent text-warm font-mono focus:outline-none" />
                        </div>
                        <div className="text-[10px] text-elegant mt-1">Verrà proposto come prezzo iniziale quando aggiungi questo piatto a un menu. Modificabile al volo.</div>
                    </div>

                    {/* Ingredienti */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-2">Ingredienti</div>
                        <div className="flex flex-wrap gap-1 mb-2">
                            {form.ingredients?.map((ing, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-deep border border-[#1f2c44] text-xs text-warm">
                                    {ing}
                                    <button type="button" onClick={() => removeIngredient(idx)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input value={newIng} onChange={(e) => setNewIng(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIngredient())}
                                placeholder="Aggiungi ingrediente…" maxLength={60}
                                className="flex-1 h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm focus:border-amber-500 outline-none" />
                            <button type="button" onClick={addIngredient}
                                className="h-10 px-3 border border-amber-500/40 text-amber-300 hover:bg-amber-900/30 rounded text-xs uppercase font-bold">
                                + Aggiungi
                            </button>
                        </div>
                    </div>

                    {/* Allergeni */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-2">Allergeni</div>
                        <div className="flex flex-wrap gap-1">
                            {ALLERGEN_OPTIONS.map((a) => {
                                const active = form.allergens?.includes(a.code);
                                return (
                                    <button key={a.code} type="button" onClick={() => toggleAllergen(a.code)}
                                        className={`px-2 py-1 rounded text-[10px] uppercase font-bold border ${active ? "bg-red-500 text-deep border-red-500" : "border-[#1f2c44] text-elegant hover:border-red-500/60"}`}>
                                        {a.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tag */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-2">Tag</div>
                        <div className="flex flex-wrap gap-1">
                            {availableTags.map((t) => {
                                const active = form.tags?.some((x) => x.toLowerCase() === t.toLowerCase());
                                return (
                                    <button key={t} type="button" onClick={() => toggleTag(t)}
                                        className={`px-2 py-1 rounded text-[10px] uppercase font-bold border ${active ? "bg-purple-500 text-deep border-purple-500" : "border-[#1f2c44] text-elegant hover:border-purple-500/60"}`}>
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 mt-5 pt-4 border-t border-[#1f2c44]">
                    <button onClick={onClose} className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                    <button onClick={submit} disabled={busy} data-testid="editor-save"
                        className="flex-1 h-12 bg-brand text-deep font-bold rounded uppercase tracking-wider text-xs disabled:opacity-60">
                        {busy ? "Salvataggio…" : (isNew ? "Crea piatto" : "Salva modifiche")}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============ ADD TO MENU MODAL ============
const AddToMenuModal = ({ dish, onClose, onAdded }) => {
    const [menus, setMenus] = useState([]);
    const [categories, setCategories] = useState([]);
    const [menuId, setMenuId] = useState("");
    const [catId, setCatId] = useState("");
    const [newCatName, setNewCatName] = useState("");
    const [price, setPrice] = useState(dish.suggested_price || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/menus").then(({ data }) => {
            setMenus(data || []);
            const active = data?.find((m) => m.is_active);
            if (active) setMenuId(active.id);
            else if (data?.[0]) setMenuId(data[0].id);
        });
    }, []);

    useEffect(() => {
        if (!menuId) return;
        api.get(`/menu/categories?menu_id=${menuId}`).then(({ data }) => setCategories(data || []));
    }, [menuId]);

    const submit = async () => {
        const p = parseFloat(price);
        if (!p || p <= 0) { toast.error("Prezzo obbligatorio"); return; }
        if (!catId && !newCatName.trim()) { toast.error("Seleziona o crea una categoria"); return; }
        setBusy(true);
        try {
            await api.post(`/dish-library/${dish.id}/add-to-menu`, {
                menu_id: menuId || null,
                category_id: catId || null,
                category_name: catId ? null : newCatName.trim(),
                price: p,
            });
            toast.success(`"${dish.name}" aggiunto al menu (€${p.toFixed(2)})`);
            onAdded();
        } catch (e) { toast.error(formatError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="addmenu-modal">
            <div className="bg-surface border-2 border-emerald-500/70 rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center gap-3 mb-4">
                    <ListPlus className="w-7 h-7 text-emerald-300" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300">Aggiungi al menu</div>
                        <div className="font-display text-lg font-bold">{dish.name}</div>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-1">Menu di destinazione</div>
                        <select value={menuId} onChange={(e) => { setMenuId(e.target.value); setCatId(""); }}
                            data-testid="addmenu-menu-select"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm">
                            {menus.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_active && " (attivo)"}</option>)}
                        </select>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-1">Categoria</div>
                        <select value={catId} onChange={(e) => setCatId(e.target.value)}
                            data-testid="addmenu-cat-select"
                            className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm mb-2">
                            <option value="">— Nuova categoria —</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {!catId && (
                            <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                                placeholder="Nome nuova categoria (es. Primi del giorno)"
                                data-testid="addmenu-newcat-input"
                                className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm" />
                        )}
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-1">
                            Prezzo {dish.suggested_price && <span className="text-[10px] opacity-70 normal-case">(suggerito: € {dish.suggested_price.toFixed(2)})</span>}
                        </div>
                        <div className="flex items-center bg-deep border border-[#1f2c44] rounded">
                            <span className="px-3 text-elegant">€</span>
                            <input type="number" step="0.10" min="0"
                                value={price} onChange={(e) => setPrice(e.target.value)}
                                placeholder="0,00" autoFocus
                                data-testid="addmenu-price"
                                className="flex-1 h-11 bg-transparent text-warm font-mono focus:outline-none" />
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 mt-5 pt-4 border-t border-[#1f2c44]">
                    <button onClick={onClose} disabled={busy} className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                    <button onClick={submit} disabled={busy} data-testid="addmenu-confirm"
                        className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded uppercase tracking-wider text-xs disabled:opacity-60">
                        {busy ? "Aggiungo…" : "Aggiungi al menu"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============ CONFIRM MODAL ============
const ConfirmModal = ({ title, icon: Icon, iconColor, borderColor, body, confirmLabel, confirmColor, onCancel, onConfirm, testid }) => (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" data-testid={testid}>
        <div className={`bg-surface border-2 ${borderColor} rounded-lg p-6 max-w-md w-full`}>
            <div className="flex items-center gap-3 mb-3">
                <Icon className={`w-7 h-7 ${iconColor}`} />
                <div className="font-display text-xl font-bold">{title}</div>
            </div>
            <div className="text-sm text-warm mb-5">{body}</div>
            <div className="flex gap-2">
                <button onClick={onCancel} className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant">Annulla</button>
                <button onClick={onConfirm} className={`flex-1 h-12 ${confirmColor} font-bold rounded uppercase tracking-wider text-xs`}>
                    {confirmLabel}
                </button>
            </div>
        </div>
    </div>
);

export default PiattiLibrary;
